// src/components/Pentests/tabs/Runs.js
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Activity, AlertTriangle, ChevronRight, CheckCircle2, Clock, Trash2, XCircle } from 'lucide-react';
import { pentestsAPI } from '../../../services/api';
import ConfirmModal from '../../common/ConfirmModal';
import { RunProgressPill } from '../runs/RunProgress';
import { moduleLabel } from '../runs/moduleLabels';
import { formatAbsoluteDate } from '../../../utils/time';

const KIND_LABEL = {
  recon: 'Recon',
  port_scan: 'Port Scan',
  vuln_scan: 'Vuln Scan',
};

const STATUS_PILL = {
  pending: { label: 'Pending', color: 'bg-slate-100 text-slate-700', icon: Clock },
  running: { label: 'Running', color: 'bg-cyan-100 text-cyan-700 animate-pulse', icon: Activity },
  completed: { label: 'Completed', color: 'bg-wasabi-100 text-wasabi-700', icon: CheckCircle2 },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-700', icon: AlertTriangle },
  timeout: { label: 'Timeout', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle },
};

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'timeout']);
const KIND_OPTIONS = ['recon', 'port_scan', 'vuln_scan'];

function fmt(ts) {
  return ts ? formatAbsoluteDate(ts) : '-';
}

export default function Runs() {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);
  const navigate = useNavigate();

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeKinds, setActiveKinds] = useState(new Set(KIND_OPTIONS));
  const [confirmDelete, setConfirmDelete] = useState({ open: false, run: null });
  const [deleting, setDeleting] = useState(false);
  const pollRef = useRef(null);

  const fetchRuns = useCallback(async () => {
    try {
      const data = await pentestsAPI.runs.list(pentestId);
      setRuns(data || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    const anyActive = runs.some((r) => !TERMINAL_STATUSES.has(r.status));
    if (anyActive) {
      pollRef.current = setInterval(fetchRuns, 5000);
      return () => {
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = null;
      };
    }
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [runs, fetchRuns]);

  const toggleKind = (k) => {
    setActiveKinds((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const handleDeleteClick = (run, e) => {
    e.stopPropagation();
    setConfirmDelete({ open: true, run });
  };

  const handleDeleteConfirm = async () => {
    const target = confirmDelete.run;
    if (!target) return;
    setDeleting(true);
    try {
      await pentestsAPI.runs.remove(pentestId, target.kind, target.id);
      setRuns((prev) => prev.filter((r) => !(r.kind === target.kind && r.id === target.id)));
      setConfirmDelete({ open: false, run: null });
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const filtered = runs.filter((r) => activeKinds.has(r.kind));

  if (loading) return <div className="text-slate-500">Loading runs...</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {KIND_OPTIONS.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => toggleKind(k)}
            className={`px-3 py-1 rounded-full text-xs border transition ${
              activeKinds.has(k)
                ? 'border-shinobi-green/60 bg-wasabi-100 text-wasabi-700'
                : 'border-cyber-300/40 bg-cyber-50/40 text-slate-500 hover:border-cyber-300'
            }`}
          >
            {KIND_LABEL[k]}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 p-10 text-center text-slate-500">
          No tool runs match the selected filters. Launch one from the Tools tab.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-cyber-300/40 bg-cyber-50/60">
          <table className="w-full text-sm text-left text-slate-800">
            <thead className="bg-cyber-200/60 text-xs uppercase tracking-wider text-slate-700">
              <tr>
                <th className="px-4 py-3">Kind</th>
                <th className="px-4 py-3">Module / Profile</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3">Completed</th>
                <th className="px-4 py-3 text-right">Findings</th>
                <th className="px-4 py-3 w-10"></th>
                <th className="px-4 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const pill = STATUS_PILL[r.status] || { label: r.status, color: 'bg-slate-100 text-slate-700', icon: Activity };
                const Icon = pill.icon;
                const canDelete = TERMINAL_STATUSES.has(r.status);
                return (
                  <tr
                    key={`${r.kind}-${r.id}`}
                    onClick={() => navigate(`/pentests/${pentestId}/runs/${r.kind}/${r.id}`)}
                    className="border-t border-cyber-300/30 hover:bg-cyber-200/40 cursor-pointer"
                  >
                    <td className="px-4 py-3 text-slate-700">{KIND_LABEL[r.kind] || r.kind}</td>
                    <td className="px-4 py-3 text-sm text-slate-800">{moduleLabel(r.module_or_profile)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.target}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-0.5">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs ${pill.color}`}>
                          <Icon className="w-3 h-3" />
                          {pill.label}
                        </span>
                        {!TERMINAL_STATUSES.has(r.status) && <RunProgressPill progress={r.progress} />}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(r.started_at)}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(r.completed_at)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{r.finding_count}</td>
                    <td className="px-2 py-3">
                      <button
                        type="button"
                        disabled={!canDelete}
                        onClick={(e) => handleDeleteClick(r, e)}
                        title={canDelete ? 'Delete run' : 'Run is still in progress'}
                        className="p-1 rounded text-slate-500 hover:text-red-700 hover:bg-red-100 disabled:opacity-30 disabled:hover:text-slate-500 disabled:hover:bg-transparent"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                    <td className="px-2 py-3 text-slate-500"><ChevronRight className="w-4 h-4" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmDelete.open}
        onClose={() => !deleting && setConfirmDelete({ open: false, run: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete run"
        message={
          confirmDelete.run
            ? `Delete this ${KIND_LABEL[confirmDelete.run.kind] || confirmDelete.run.kind} run (${moduleLabel(confirmDelete.run.module_or_profile)})? Findings only seen in this run will be removed; findings seen across multiple runs will be preserved.`
            : ''
        }
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
}
